/**
 * The glyphs folder 52's buttons and ⋯ draw, lifted from the boards' own HTML
 * (`design/mockups/52-job-stages/html/js-01-job-buttons.html`,
 * `js-02-quote-buttons.html`) — the visit glyphs' pattern (`visit/glyphs.tsx`):
 * the paths are data, so the board's own send arrow and turn-back arrow are
 * drawn rather than the nearest library icon (`build-exactly-as-drawn.md` law 2).
 *
 * Sizes are the boards': 14.5 on a 36 px button, 17 in a ⋯ row, 13 in the
 * meta line, 16 for the ⋯ itself (its dots at 2.4). Stroke 2 everywhere else.
 */

interface GlyphSpec {
  paths: string[]
  circles?: { cx: number; cy: number; r: number }[]
  rects?: { x: number; y: number; width: number; height: number; rx: number }[]
}

export const STAGE_GLYPHS = {
  /** Edit — the job's details. */
  pencil: { paths: ['M4 20l1-4L16.5 4.5a2 2 0 0 1 3 3L8 19z', 'M14 7l3 3'] },
  /** New quote. */
  plus: { paths: ['M12 5v14M5 12h14'] },
  /** Mark quoted; the meta line's quote with the customer. */
  send: { paths: ['M22 2L11 13M22 2l-7 20-4-9-9-4z'] },
  /** Open quote · Open draft · Open the agreement. */
  doc: { paths: ['M6 3h8l4 4v14H6z', 'M14 3v4h4M9 12h6M9 16h6'] },
  /** Mark declined · Cancel job. */
  close: { paths: ['M6 6l12 12M18 6L6 18'] },
  /** Withdraw quote · Reopen request. */
  rotate: { paths: ['M4 12a8 8 0 1 0 3-6.2L4 8M4 3v5h5'] },
  /** Start work. */
  play: { paths: ['M7 5l12 7-12 7z'] },
  /** Approve · Complete. */
  check: { paths: ['M5 12.5l4.5 4.5L19 7.5'] },
  /** Schedule · Check crew availability. */
  calendar: { paths: ['M3 10h18M8 3v4M16 3v4'], rects: [{ x: 3, y: 5, width: 18, height: 16, rx: 2 }] },
  /** Change order (folder 51). */
  swap: { paths: ['M7 7h12l-3-3M17 17H5l3 3'] },
  /** Preview. */
  eye: { paths: ['M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z'], circles: [{ cx: 12, cy: 12, r: 3 }] },
  /** The meta line's *<n> of <m> visits booked*. */
  truck: {
    paths: ['M3 6h11v10H3zM14 10h4l3 3v3h-7'],
    circles: [{ cx: 7, cy: 18, r: 1.8 }, { cx: 17, cy: 18, r: 1.8 }],
  },
  /** The ⋯ — vertical, at stroke 2.4. */
  more: { paths: [], circles: [{ cx: 12, cy: 5, r: 1.2 }, { cx: 12, cy: 12, r: 1.2 }, { cx: 12, cy: 19, r: 1.2 }] },
  /* Board 3's (C52a, additive): the bands' ⓘ, Proof's paperclip, Who approved's chevron. */
  /** A band's lead — 15 at stroke 2. */
  info: { paths: ['M12 11v6M12 7.5v.01'], circles: [{ cx: 12, cy: 12, r: 9 }] },
  /** Proof — *Attach a photo, screenshot or email*. */
  attach: { paths: ['M21 11.5l-8.6 8.6a5.2 5.2 0 0 1-7.4-7.4l8.6-8.6a3.5 3.5 0 0 1 5 5l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9'] },
  /** Who approved's picker. */
  chevronDown: { paths: ['M6 9l6 6 6-6'] },
} satisfies Record<string, GlyphSpec>

export type StageGlyphName = keyof typeof STAGE_GLYPHS

/** One glyph, at the size and stroke the board draws it, in the current colour. */
export function StageGlyph({ name, size, stroke = 2 }: { name: StageGlyphName; size: number; stroke?: number }) {
  const glyph: GlyphSpec = STAGE_GLYPHS[name]
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      className="shrink-0"
      style={{
        stroke: 'currentColor', fill: 'none', strokeWidth: stroke, strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    >
      {glyph.rects?.map((rect) => <rect key={`${rect.x}-${rect.y}`} {...rect} />)}
      {glyph.circles?.map((circle) => (
        <circle key={`${circle.cx}-${circle.cy}`} cx={circle.cx} cy={circle.cy} r={circle.r} />
      ))}
      {glyph.paths.map((path) => <path key={path} d={path} />)}
    </svg>
  )
}
